import { beautifyTableLabel } from '@carboncopyco/editor-utils';
import type { TableCraftFunction, TableCraftRow } from '@carboncopyco/types';
import { LoadingDots } from '@carboncopyco/ui';
import { useState } from 'react';
import { RowOptionsTd } from './RowOptions';
import { Td } from './Td';
import { Th } from './Th';

type TableProps = {
  objectId: string;
  rows: TableCraftRow[];
  onNavigate: (tableId: string) => void;
  onRevert: ({
    entity,
    row_id,
    col_name,
  }: {
    entity: 'cell' | 'column' | 'row';
    row_id?: string;
    col_name?: string;
  }) => void;
  selectableColumns?: boolean;
  onCellUpdate: ({
    objectId,
    column,
    value,
    row_id,
    id_column,
  }: {
    objectId: string;
    column: string;
    value: string | boolean;
    row_id: string;
    id_column: string;
  }) => Promise<void>;
  onColumnUpdate: ({
    objectId,
    columnKey,
    type,
    data,
  }: {
    objectId: string;
    columnKey: string;
    type: 'AI' | 'hide' | 'set-object-pk' | 'function';
    data: unknown;
  }) => Promise<void>;
  searchTerm?: string;
  disabledColumns?: string[];
  isLoading?: boolean;
  tableCraftFunctions?: TableCraftFunction[];
};

export const ObjectsTable = ({
  objectId,
  rows,
  onNavigate,
  onCellUpdate,
  onColumnUpdate,
  selectableColumns,
  onRevert,
  searchTerm,
  disabledColumns,
  isLoading,
  tableCraftFunctions,
}: TableProps) => {
  const [selectedColumn, setSelectedColumn] = useState<string>();
  const [selectedRow, setSelectedRow] = useState<string>();

  const firstRow = rows[0];
  const firstRowIdColumn = firstRow?.id_column;
  const rowsData = (rows ?? []).map((row) => row.data);
  const objectKeys = Object.keys(rowsData[0] ?? {});
  const sortedObjectKeys = objectKeys.sort((a, b) => {
    if (a === firstRowIdColumn) {
      return -1;
    }
    if (b === firstRowIdColumn) {
      return 1;
    }
    return a.localeCompare(b);
  });
  const columns = sortedObjectKeys.map(beautifyTableLabel);

  const columnValuesByKeys = sortedObjectKeys.reduce(
    (acc, key) => {
      const originalValues = rows.map((row) => row.original_data[key]);
      const values = rows.map((row) => row.data[key]);
      acc[key] = { originalValues, values };
      return acc;
    },
    {} as Record<string, { originalValues: string[]; values: string[] }>
  );

  if (!sortedObjectKeys.length || !rows.length) {
    return isLoading ? (
      <div className="flex grow shrink basis-0 min-w-0 bg-white/50 items-center justify-center">
        <LoadingDots />
      </div>
    ) : (
      <div className="flex justify-center items-center h-20 text-ds-natural-600">
        No data
      </div>
    );
  }

  return (
    <div className="flex grow shrink basis-0 min-w-0 items-start min-h-0 relative">
      <div className="grid rounded-ds-md min-w-0 min-h-0 max-h-full overflow-auto max-w-full w-full border border-solid border-ds-natural-150">
        <table className="border-0 border-spacing-0 text-left overflow-hidden -mt-px -ml-px">
          <thead>
            <tr className="bg-ds-natural-100">
              {columns.map((label, index) => {
                const columnKey = sortedObjectKeys[index];
                const columnValues = columnValuesByKeys[columnKey].values;
                const originalColumnValues =
                  columnValuesByKeys[columnKey].originalValues;
                const isColumnSelected = selectedColumn === columnKey;
                const isColumnModified = rows.some((row) =>
                  row.modified_cols.includes(columnKey)
                );

                return (
                  <>
                    {index === 0 && (
                      <th className="py-0 px-2 h-7 border-t border-l border-solid border-ds-natural-150 w-[34px] min-w-[34px]" />
                    )}
                    <Th
                      columnValues={columnValues}
                      columnKey={columnKey}
                      label={label}
                      isSelected={isColumnSelected}
                      isModified={isColumnModified}
                      isDisabled={disabledColumns?.some(
                        (col) => col === columnKey
                      )}
                      selectable={selectableColumns}
                      onAiResponse={(prompt) => {
                        const response = onColumnUpdate({
                          objectId,
                          columnKey,
                          type: 'AI',
                          data: {
                            originalColumnValues,
                            prompt,
                          },
                        });
                        setSelectedColumn(undefined);
                        return response;
                      }}
                      onColumnRevert={() => {
                        onRevert({
                          entity: 'column',
                          col_name: columnKey,
                        });
                      }}
                      onSetColumnAsKey={() => {
                        onColumnUpdate({
                          objectId,
                          columnKey,
                          type: 'set-object-pk',
                          data: columnKey,
                        });
                      }}
                      onColumnHide={() => {
                        onColumnUpdate({
                          objectId,
                          columnKey,
                          type: 'hide',
                          data: columnKey,
                        });
                      }}
                      onSelectedColumnChange={(key) => {
                        setSelectedColumn(key);
                        setSelectedRow(undefined);
                      }}
                      onDataFunctionsClick={({
                        columnKey: key,
                        selectedFn,
                        params,
                      }) => {
                        onColumnUpdate({
                          objectId,
                          columnKey: key,
                          type: 'function',
                          data: { selectedFn, params },
                        });
                      }}
                      tableCraftFunctions={tableCraftFunctions}
                    />
                  </>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              return (
                <tr key={row.id_value}>
                  {sortedObjectKeys.map((column, index) => {
                    const value = row.data[column];
                    const isRowModified = row.modified_cols.length > 0;

                    const isColumnSelected =
                      selectedColumn === sortedObjectKeys[index];
                    const isRowSelected = selectedRow === row.id_value;
                    const isLastRow = rowIndex === rows.length - 1;
                    const isLastColumn = index === sortedObjectKeys.length - 1;

                    const isStringOrNumberValue = ['string', 'number'].includes(
                      typeof value
                    );

                    const matchesSearch =
                      searchTerm && isStringOrNumberValue
                        ? value
                            .toString()
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                        : false;

                    return (
                      <>
                        {index === 0 && (
                          <RowOptionsTd
                            row={row}
                            isRowSelected={isRowSelected}
                            isRowModified={isRowModified}
                            onSelectedRowChange={(rowId) => {
                              setSelectedRow(rowId);
                              setSelectedColumn(undefined);
                            }}
                            onRevert={onRevert}
                          />
                        )}
                        <Td
                          key={column}
                          value={value}
                          onNavigate={onNavigate}
                          onSubmit={(updatedValue) =>
                            onCellUpdate({
                              objectId,
                              column,
                              value: updatedValue,
                              row_id: row.id_value,
                              id_column: row.id_column,
                            })
                          }
                          onRevert={() =>
                            onRevert({
                              entity: 'cell',
                              row_id: row.id_value,
                              col_name: column,
                            })
                          }
                          isColumnSelected={isColumnSelected}
                          isRowSelected={isRowSelected}
                          isModified={row.modified_cols.includes(column)}
                          isDisabled={disabledColumns?.some(
                            (col) => col === column
                          )}
                          isLastRow={isLastRow}
                          isLastColumn={isLastColumn}
                          matchesSearch={matchesSearch}
                        />
                      </>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <LoadingDots />
          </div>
        )}
      </div>
    </div>
  );
};

ObjectsTable.displayName = 'ObjectsTable';
